/* eslint-disable sort-keys */

import {
    Route,
    RouteParamsList,
    TranscriptParams,
} from 'navigation/types'

import {
    useAppDispatch,
    useAppSelector,
} from 'hooks'

import {
    useEffect,
    useState,
} from 'react'

import {QueueProps} from 'components/Queue/types'

import {describeChannel} from 'api/action/describeChannel'
import {describeQueue} from 'api/action/describeQueue'
import {describeTranscript} from 'api/action/describeTranscript'
import {
    getPermissions,
} from 'selectors/ninchat/permissions'
import permissionGuard from 'components/Navigation/helpers/PermissionGuard'
import {updateRealm} from 'state/ui'
import {useRouting} from 'navigation/hooks'
import {useSelector} from 'react-redux'

export interface Permissions {
  view_realm: boolean
  manage_realm: boolean
  view_realm_site: boolean
  manage_realm_site: boolean
  view_realm_queue: boolean
  manage_realm_queue: boolean
  update_queue_closed: boolean
  view_realm_statistics: boolean
  view_realm_statistics_agent: boolean
  view_realm_activity: boolean
  view_realm_transcript: boolean
  delete_realm_transcript: boolean
  view_realm_metadata: boolean
}


export const usePermissions = (route: Route) => {
    const {replaceRoute} = useRouting()
    const dispatch = useAppDispatch()

    const [lastRealmId, setLastRealmId] = useState('')
    const [lastRouteName, setLastRouteName] = useState('')
    const [loading, setLoading] = useState(true)

    const fullState = useAppSelector((st) => st)
    const realmId = useAppSelector(({ui}) => ui.realm_id) || ''

    const permissions = useSelector((state) => getPermissions(state, realmId))

    const getTranscriptDetails = async () => {
        // eslint-disable-next-line no-extra-parens
        const audienceId = route.name === 'Transcript' ? (route.params as TranscriptParams)?.audience_id : ''
        if (audienceId) {
            try {
                const event = await dispatch(describeTranscript({audience_id: audienceId})).unwrap()
                permissionGuard.setChannelId(event?.channel_id || '')
                await dispatch(describeChannel({channel_id: event?.channel_id || ''})).unwrap()
                setLoading(false)
            } catch (error) {
                setLoading(false)
            }
        }
        setLoading(false)
    }

    useEffect(() => {
        setLastRealmId(realmId)
        setLastRouteName(route?.name)
        setLoading(true)

        if (!route) {
            setLoading(false)
            return
        }

        if (route.name === 'Transcript') {
            getTranscriptDetails()
        } else if (route.name === 'Queue') {
            // eslint-disable-next-line no-extra-parens
            const queueId = (route.params as RouteParamsList['Queue'])?.queue_id

            if (fullState?.ninchat?.user_queues?.[queueId || ''] === undefined) {
                dispatch(describeQueue({queue_id: queueId})).unwrap()
                    .then((queueFound) => {
                        // handle situation when user is not a queue member just have permissions to view a queue
                        // eslint-disable-next-line no-extra-parens
                        const queueRealmId = (queueFound as QueueProps).realm_id
                        if (fullState?.ninchat.user_realms?.[queueRealmId]?.realm_attrs?.permissions_enabled &&
                            fullState.ninchat.session_realms_permissions?.[queueRealmId as any]?.indexOf('view_realm_activity') !== -1) {
                            dispatch(updateRealm(queueRealmId))
                        }
                        setLoading(false)
                    }, () => {
                        setLoading(false)
                    })
            } else {
                setLoading(false)
            }
        } else {
            setLoading(false)
        }
    }, [realmId, route])

    const permObj = {
        // here we can mock permissions
        // delete_realm_transcript: true,
        // manage_realm: true,
        // manage_realm_queue: false,
        // update_queue_closed: false,
        // view_realm: true,
        // view_realm_activity: true,
        // view_realm_metadata: true,
        // view_realm_queue: false,
        // view_realm_site: false,
        // view_realm_statistics: true,
        // view_realm_transcript: false,
        delete_realm_transcript: permissions?.indexOf('delete_realm_transcript') !== -1,
        manage_realm: permissions?.indexOf('manage_realm') !== -1,
        manage_realm_queue: permissions?.indexOf('manage_realm_queue') !== -1,
        manage_realm_site: permissions?.indexOf('manage_realm_site') !== -1,
        update_queue_closed: permissions?.indexOf('update_queue_closed') !== -1,
        view_realm: permissions?.indexOf('view_realm') !== -1,
        view_realm_activity: permissions?.indexOf('view_realm_activity') !== -1,
        view_realm_metadata: permissions?.indexOf('view_realm_metadata') !== -1,
        view_realm_queue: permissions?.indexOf('view_realm_queue') !== -1,
        view_realm_site: permissions?.indexOf('view_realm_site') !== -1,
        view_realm_statistics: permissions?.indexOf('view_realm_statistics') !== -1,
        view_realm_statistics_agent: permissions.includes('view_realm_statistics_agent'),
        view_realm_transcript: permissions?.indexOf('view_realm_transcript') !== -1,
    }

    permissionGuard.setPermissions(permObj)
    permissionGuard.setRoute(route as Route, replaceRoute)
    permissionGuard.setState(fullState)
    permissionGuard.setRealmId(realmId)

    return {
        // eslint-disable-next-line no-extra-parens
        loading: loading || realmId !== lastRealmId || route?.name !== lastRouteName,
        permissions: permObj,
    }
}
